import { Weddings } from '@bridebook/models';
import type { ISupplier_Type } from '@bridebook/models/source/models/Suppliers.types';
import {
  IInteraction,
  IInteractionAction,
} from '@bridebook/models/source/models/Weddings/Interactions.types';
import { Interaction } from '@bridebook/models/source/models/Weddings/Interactions';
import type { VertexAIResponse } from './make-prediction';
import fetchSuppliersByIds from '@bridebook/toolbox/src/search-suppliers/fetch-suppliers-by-ids';
import { CountryCodes } from '@bridebook/toolbox/src/gazetteer';

import type { WeddingFeatures } from '@bridebook/toolbox/src/recommendations/wedding-features';

export type PredictRequest = {
  weddingId: string;
  size: number;
  supplierId?: string;
  countryCode?: CountryCodes;
} & WeddingFeatures;

export type PredictResponse = Awaited<ReturnType<typeof fetchSuppliersByIds>>;

export const fetchWeddingInteractionsData = async (
  weddingId: string,
  shouldFetchInteractions = true,
) => {
  const interactionsRef = Weddings._.getById(weddingId).Interactions;
  const suppliersRef = Weddings._.getById(weddingId).Suppliers;
  const supplierType = 'venue' as ISupplier_Type;

  const [interactions, shortlisted, ignored] = await Promise.all([
    shouldFetchInteractions
      ? interactionsRef.getBySupplier(undefined, supplierType)
      : Promise.resolve(null),
    suppliersRef.getShortlisted(supplierType),
    suppliersRef.getIgnored(supplierType),
  ]);

  return { interactions, shortlisted, ignored };
};

export interface SupplierHistory {
  user_history: string[];
  action_history: string[];
}

export const getSupplierHistory = (
  supplierId: string | undefined,
  interactions: Record<string, IInteraction> | null,
): SupplierHistory => {
  if (supplierId) {
    // Used to recommend similar suppliers
    return { user_history: [supplierId], action_history: ['view'] };
  }
  return extractSupplierHistory(interactions as Record<string, IInteraction>);
};

export const extractSupplierHistory = (
  interactions: Record<string, IInteraction>,
): SupplierHistory => {
  const user_history: string[] = [];
  const action_history: string[] = [];

  for (const interaction of Object.values(interactions)) {
    user_history.push(interaction.supplier.id);

    if (interaction.action === IInteractionAction.ENQUIRY && Interaction.fcr(interaction)) {
      action_history.push('FCR');
    } else {
      action_history.push(interaction.action);
    }
  }

  return { user_history, action_history };
};

export const processPredictions = (
  response: VertexAIResponse,
  excludedIds: string[],
  size: number,
): string[] =>
  (response.predictions[0] ?? [])
    .filter((id) => !excludedIds.includes(id)) // Exclude ignored and shortlisted supplier IDs
    .slice(0, size);
